import { createTheme, SimplePaletteColorOptions, responsiveFontSizes } from '@material-ui/core'

type BadgeColorStyle = {
  info: SimplePaletteColorOptions,
  error: SimplePaletteColorOptions,
  success: SimplePaletteColorOptions,
  warning: SimplePaletteColorOptions
}

declare module '@material-ui/core/styles' {
  interface TypographyVariants {
    airbnbH1: React.CSSProperties,
    airbnbH2: React.CSSProperties,
    airbnbH3: React.CSSProperties,
    airbnbH4: React.CSSProperties,
    airbnbH5: React.CSSProperties,
    airbnbBody1: React.CSSProperties,
    airbnbBody2: React.CSSProperties,
    airbnbBody3: React.CSSProperties
  }
  interface TypographyVariantsOptions {
    airbnbH1?: React.CSSProperties,
    airbnbH2?: React.CSSProperties,
    airbnbH3?: React.CSSProperties,
    airbnbH4?: React.CSSProperties,
    airbnbH5?: React.CSSProperties,
    airbnbBody1?: React.CSSProperties,
    airbnbBody2?: React.CSSProperties,
    airbnbBody3?: React.CSSProperties
  }
}
declare module '@material-ui/core/Typography' {
  interface TypographyPropsVariantOverrides {
    h1: true,
    airbnbH1: true,
    airbnbH2: true,
    airbnbH3: true,
    airbnbH4: true,
    airbnbH5: true,
    airbnbBody1: true,
    airbnbBody2: true,
    airbnbBody3: true
  }
}

declare module '@material-ui/core/styles/createPalette' {
  export interface PaletteOptions {
    pink: PaletteColorOptions,
    checkGreen: PaletteColorOptions,
    purple: PaletteColorOptions,
    blue: PaletteColorOptions,
    gray: PaletteColorOptions,
    orange: PaletteColorOptions,
    green: PaletteColorOptions,
    scoreA: PaletteColorOptions,
    scoreB: PaletteColorOptions,
    scoreC: PaletteColorOptions,
    scoreD: PaletteColorOptions,
    scoreE: PaletteColorOptions,
    scoreF: PaletteColorOptions,
    pastelPink: PaletteColorOptions,
    pastelPurple: PaletteColorOptions,
    pastelGray: PaletteColorOptions,
    pastelBlue: PaletteColorOptions,
    pastelOrange: PaletteColorOptions,
    pastelGreen: PaletteColorOptions,
    mutualBlue: PaletteColorOptions,
    textBlack: PaletteColorOptions,
    textGray1: PaletteColorOptions,
    textGray2: PaletteColorOptions,
    textGray3: PaletteColorOptions,
    textGray4: PaletteColorOptions,
    textInverse: PaletteColorOptions,
    badgeTitleText: BadgeColorStyle,
    badgeDescriptionText: BadgeColorStyle,
    badgeBorderColor: BadgeColorStyle,
    badgeBackgroundColor: BadgeColorStyle
  }

  export interface Palette {
    pink: PaletteColor,
    purple: PaletteColor,
    blue: PaletteColor,
    gray: PaletteColor,
    checkGreen: PaletteColor,
    orange: PaletteColor,
    green: PaletteColor,
    scoreA: PaletteColor,
    scoreB: PaletteColor,
    scoreC: PaletteColor,
    scoreD: PaletteColor,
    scoreE: PaletteColor,
    scoreF: PaletteColor,
    pastelPink: PaletteColor,
    pastelPurple: PaletteColor,
    pastelGray: PaletteColor,
    pastelBlue: PaletteColor,
    pastelOrange: PaletteColor,
    pastelGreen: PaletteColor,
    mutualBlue: PaletteColor,
    textBlack: PaletteColor,
    textGray1: PaletteColor,
    textGray2: PaletteColor,
    textGray3: PaletteColor,
    textGray4: PaletteColor,
    textInverse: PaletteColor,
    badgeTitleText: BadgeColorStyle,
    badgeDescriptionText: BadgeColorStyle,
    badgeBorderColor: BadgeColorStyle,
    badgeBackgroundColor: BadgeColorStyle
  }
}

declare module '@material-ui/core/styles/createTheme' {
  interface Theme {
    sidebar: {
      minimizedWidthMdDown: number,
      minimizedWidthMdUp: number,
      expandedWidth: number,
      backgroundColor: string
    },
    topbar: {
      height: number,
      backgroundColor: string
    },
    boxShadow: {
      container: string,
      sideModal: string,
      centeredModal: string
    }
  }
  interface ThemeOptions {
    sidebar?: {
      minimizedWidthMdDown?: number,
      minimizedWidthMdUp?: number,
      expandedWidth?: number,
      backgroundColor?: string
    },
    topbar?: {
      height?: number,
      backgroundColor?: string
    },
    boxShadow?: {
      container?: string,
      sideModal?: string,
      centeredModal?: string
    }
  }
}

const theme = createTheme({
  typography: {
    fontFamily: 'Lato,sans-serif',
    airbnbH1: {
      fontFamily: 'Airbnb',
      fontWeight: 500,
      fontSize: 32
    },
    airbnbH2: {
      fontFamily: 'Airbnb',
      fontWeight: 500,
      fontSize: 28
    },
    airbnbH3: {
      fontFamily: 'Airbnb',
      fontWeight: 500,
      fontSize: 24
    },
    airbnbH4: {
      fontFamily: 'Airbnb',
      fontWeight: 500,
      fontSize: 20
    },
    airbnbH5: {
      fontFamily: 'Airbnb',
      fontWeight: 500,
      fontSize: 18
    },
    airbnbBody1: {
      fontFamily: 'Airbnb',
      fontWeight: 400,
      fontSize: 16
    },
    airbnbBody2: {
      fontFamily: 'Airbnb',
      fontWeight: 400,
      fontSize: 14
    },
    airbnbBody3: {
      fontFamily: 'Airbnb',
      fontWeight: 400,
      fontSize: 12
    },
    h1: {
      fontFamily: 'Lato,sans-serif',
      fontSize: 32
    },
    h2: {
      fontFamily: 'Lato,sans-serif',
      fontSize: 28
    },
    h3: {
      fontFamily: 'Lato,sans-serif',
      fontSize: 24
    },
    h4: {
      fontFamily: 'Lato,sans-serif',
      fontSize: 20
    },
    h5: {
      fontFamily: 'Lato,sans-serif',
      fontSize: 18
    },
    body1: {
      fontSize: 16,
      fontFamily: 'Lato,sans-serif'
    },
    body2: {
      fontSize: 14,
      fontFamily: 'Lato,sans-serif'
    }
  },
  palette: {
    primary: {
      main: '#FF4086'
    },
    secondary: {
      main: '#368DF7'
    },
    mutualBlue: {
      main: '#272976'
    },
    success: {
      main: '#D8EACC'
    },
    warning: {
      main: '#FBEECA'
    },
    error: {
      main: '#BE4716'
    },
    info: {
      main: '#C5DBF2'
    },
    pink: {
      main: '#FF4086'
    },
    purple: {
      main: '#705DC1'
    },
    gray: {
      main: '#616161'
    },
    blue: {
      main: '#4D87FF'
    },
    checkGreen: {
      main: '#05C370'
    },
    orange: {
      main: '#FF562D'
    },
    green: {
      main: '#3E995B'
    },
    pastelPink: {
      main: '#F5ECF0'
    },
    pastelPurple: {
      main: '#EFEDF5'
    },
    pastelGray: {
      main: '#EAEFF4'
    },
    pastelBlue: {
      main: '#EEF4F6'
    },
    pastelOrange: {
      main: '#F5F0ED'
    },
    pastelGreen: {
      main: '#ECF5EF'
    },
    scoreA: {
      main: '#00923F'
    },
    scoreB: {
      main: '#7ABC30'
    },
    scoreC: {
      main: '#FFCD00'
    },
    scoreD: {
      main: '#FB8F00'
    },
    scoreE: {
      main: '#F44910'
    },
    scoreF: {
      main: '#C02220'
    },
    textBlack: {
      main: '#2B2B2B'
    },
    textGray1: {
      main: '#6D6D6D'
    },
    textGray2: {
      main: '#9B9B9B'
    },
    textGray3: {
      main: '#CACBCC'
    },
    textGray4: {
      main: '#E1E1E1'
    },
    textInverse: {
      main: '#FFFFFF'
    },
    badgeTitleText: {
      error: {
        main: '#BE4716',
        light: '#FFFFFF',
        dark: '#FFFFFF'
      },
      warning: {
        main: '#CE8B07',
        light: '#FFFFFF',
        dark: '#FFFFFF'
      },
      success: {
        main: '#1B7441',
        light: '#FFFFFF',
        dark: '#FFFFFF'
      },
      info: {
        main: '#3475CF',
        light: '#FFFFFF',
        dark: '#FFFFFF'
      }
    },
    badgeDescriptionText: {
      error: {
        main: '#2B2B2B',
        light: '#FFFFFF',
        dark: '#FEE8E0'
      },
      warning: {
        main: '#2B2B2B',
        light: '#FFFFFF',
        dark: '#FFF8E4'
      },
      success: {
        main: '#2B2B2B',
        light: '#FFFFFF',
        dark: '#E9F8F0'
      },
      info: {
        main: '#2B2B2B',
        light: '#FFFFFF',
        dark: '#F4F6FA'
      }
    },
    badgeBorderColor: {
      error: {
        main: '#FBCFBD',
        light: '#BE4716',
        dark: '#BE4716'
      },
      warning: {
        main: '#FBEECA',
        light: '#CE8B07',
        dark: '#CE8B07'
      },
      success: {
        main: '#BEE3CF',
        light: '#1B7441',
        dark: '#1B7441'
      },
      info: {
        main: '#E4ECF7',
        light: '#3475CF',
        dark: '#3475CF'
      }
    },
    badgeBackgroundColor: {
      error: {
        main: '#FEE8E0',
        light: '#E2612D',
        dark: '#BE4716'
      },
      warning: {
        main: '#FFF8E4',
        light: '#F2B714',
        dark: '#CE8B07'
      },
      success: {
        main: '#E9F8F0',
        light: '#2EA561',
        dark: '#1B7441'
      },
      info: {
        main: '#F4F6FA',
        light: '#5DA0FD',
        dark: '#3475CF'
      }
    },
    grey: {
      100: '#FBFBFB',
      200: '#F4F4F4',
      300: '#EFEFEF',
      400: '#DFDFDF',
      500: '#2B2B2B'
    }
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 414,
      md: 960,
      lg: 1280,
      xl: 1920
    }
  },
  sidebar: {
    expandedWidth: 290,
    minimizedWidthMdUp: 75,
    minimizedWidthMdDown: 75,
    backgroundColor: '#272976'
  },
  topbar: {
    backgroundColor: '#ffffff',
    height: 75
  },
  boxShadow: {
    container: '1px 4px 10px rgba(0, 0, 0, 0.05)',
    centeredModal: '0px 0px 50px rgb(138, 138, 138)',
    sideModal: '0px 0px 50px rgba(137, 137, 137, 0.65)'
  },
  shape: {
    borderRadius: 8
  },
  components: {
    MuiLink: {
      styleOverrides: {
        root: {
          textDecoration: 'none'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          boxShadow: 'none',
          borderRadius: 8
        }
      }
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: '#368DF7'
          }
        }
      }
    }
  }
})

export const mutualTheme = responsiveFontSizes(theme)
